/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Table } from 'antd';
import { FaEye, FaEyeSlash, FaPen, FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import usePaginationHook from '../../../../utils/usePagination';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertConfirm, AlertSuccess } from '../../../util/Alert';
import { FormProvider, useForm } from 'react-hook-form';
import 'dayjs/locale/th';
import fitMissionServices from '../../../../services/fit-mission.services';
import {
  missionRewardBannerFormDefaultValues,
  missionRewardBannerFormSchema,
} from '../components/dto/missionRewardBanner';
import LegacyMissionRewardBannerModal from '../components/modal/LegacyMissionRewardBannerModal';

dayjs.locale('th');

const pageSize = 10;

const LegacyMissionRewardBannerPage = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const {
    data: bannerLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();

  const formMethods = useForm({
    defaultValues: missionRewardBannerFormDefaultValues,
    resolver: missionRewardBannerFormSchema(),
  });

  const _HandleModalOpen = (formType) => {
    formMethods.setValue('formType', formType);
    setModalOpen(true);
  };

  const _HandleModalClose = () => {
    formMethods.reset(missionRewardBannerFormDefaultValues);
    setModalOpen(false);
  };

  fitMissionServices.useQueryGetMissionRewardBanners(
    {
      take: pageSize,
      page,
    },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    },
  );

  const { mutate: deleteBanner } =
    fitMissionServices.useMutationDeleteMissionRewardBanner(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-mission-reward-banner'],
        refetchType: 'all',
      });
      AlertSuccess('ลบข้อมูลสำเร็จ');
    });

  const { mutate: updateDisplay } =
    fitMissionServices.useMutationUpdateMissionRewardBannerDisplay(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-mission-reward-banner'],
        refetchType: 'all',
      });
      AlertSuccess('แก้ไขการแสดงผลสำเร็จ');
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'รูปภาพ',
      dataIndex: 'image',
      key: 'image',
      render: (_, record) => (
        <div style={{ width: 150, height: 80 }}>
          {record.image && (
            <img
              alt={record.title}
              src={record.image}
              style={{
                width: 150,
                height: 80,
                objectFit: 'cover',
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => <span>{record.title}</span>,
    },
    // {
    //   title: 'รายละเอียด',
    //   dataIndex: 'description',
    //   key: 'description',
    //   render: (_, record) => (
    //     <span
    //       style={{
    //         maxWidth: 200,
    //         display: 'block',
    //         overflow: 'hidden',
    //         textOverflow: 'ellipsis',
    //         whiteSpace: 'nowrap',
    //       }}
    //     >
    //       {record.description}
    //     </span>
    //   ),
    // },
    {
      title: 'วันที่เริ่มต้น',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (_, record) => (
        <span>{dayjs(record.startDate).format('DD/MM/YYYY')}</span>
      ),
    },
    {
      title: 'วันที่สิ้นสุด',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (_, record) => (
        <span>{dayjs(record.endDate).format('DD/MM/YYYY')}</span>
      ),
    },
    {
      title: 'อัปเดตเมื่อ',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (_, record) => (
        <span>{dayjs(record.updatedAt).format('DD/MM/YYYY HH:mm')}</span>
      ),
      sorter: (a, b) =>
        dayjs(a.updatedAt).diff(dayjs(b.updatedAt), 'D') > 0 ? 1 : -1,
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {record.display ? (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('ปิดการแสดงผล');
                if (confirm) updateDisplay({ id: record.id, display: false });
              }}
            >
              <FaEye />
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('เปิดการแสดงผล');
                if (confirm) updateDisplay({ id: record.id, display: true });
              }}
            >
              <FaEyeSlash />
            </div>
          )}
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              formMethods.setValue('id', record.id);
              _HandleModalOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteBanner({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Panel>
      <PanelHeader>จัดการแบนเนอร์ของรางวัล</PanelHeader>
      <PanelBody>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}
        >
          <Button
            onClick={() => {
              _HandleModalOpen('create');
            }}
            type="primary"
          >
            <FaPlus className="mr-2" />
            เพิ่มแบนเนอร์รางวัล
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={bannerLists}
          style={{ backgroundColor: '#fff' }}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          onChange={({ current }) => {
            setPage(current);
          }}
          pagination={{
            pageSize,
            total,
          }}
        />
      </PanelBody>
      <FormProvider {...formMethods}>
        <LegacyMissionRewardBannerModal
          open={modalOpen}
          handleClose={_HandleModalClose}
        />
      </FormProvider>
    </Panel>
  );
};

export default LegacyMissionRewardBannerPage;
